import {Component, Inject, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {IdName} from '@domain/id-name';
import {MachineType} from '@domain/machine/machine-type.enum';
import {RunCarpet} from '@domain/planning-prototype/carpet/run-carpet';
import {PlanningEquipment} from '@domain/planning-prototype/planning-equipment';
import {PlanningItem} from '@domain/planning-prototype/planning-item';
import {RunPlastic} from '@domain/planning-prototype/plastic/run-plastic';
import {Tool} from '@domain/planning-prototype/plastic/tool';
import {RunTufting} from '@domain/planning-prototype/tufting/run-tufting';
import {TuftProduct} from '@domain/planning-prototype/tufting/tuft-product';
import {RunWeaving} from '@domain/planning-prototype/weaving/run-weaving';
import {WeaveProduct} from '@domain/planning-prototype/weaving/weave-product';
import {Permission} from '@domain/profile/permission.enum';
import {Subscription} from '@domain/profile/subscription';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {BaseComponent, NavigationHelperPrototypeService} from '@vdw/angular-component-library';
import {distinctUntilKeyChanged, filter, takeUntil} from 'rxjs';
import {PlanningPrototypeContextService} from '../../utilities/planning-prototype-context.service';

@Component({
  selector: 'app-machine-detail-sidebar',
  templateUrl: './machine-detail-sidebar.component.html',
  styleUrls: ['./machine-detail-sidebar.component.scss']
})
export class MachineDetailSidebarComponent extends BaseComponent implements OnInit {
  public machine: PlanningEquipment;
  private currentSubscription: Subscription;
  private activeItemOnMachine: PlanningItem;

  public get activeCreel(): IdName {
    return (this.activeItemOnMachine as RunCarpet)?.creel;
  }

  public get activeQuality(): IdName {
    return (this.activeItemOnMachine as RunCarpet)?.quality;
  }

  public get activeWeaveProduct(): WeaveProduct {
    return (this.activeItemOnMachine as RunWeaving)?.weaveProduct;
  }

  public get activeTuftProduct(): TuftProduct {
    return (this.activeItemOnMachine as RunTufting)?.tuftProduct;
  }

  public get activeTool(): Tool {
    return (this.activeItemOnMachine as RunPlastic)?.tool;
  }

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    private readonly planningContext: PlanningPrototypeContextService,
    private readonly navigationHelper: NavigationHelperPrototypeService,
    private readonly router: Router
  ) {
    super();
  }

  public ngOnInit(): void {
    this.planningContext.selectedItemChanges
      .pipe(
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((item: PlanningEquipment | PlanningItem) => item instanceof PlanningEquipment),
        distinctUntilKeyChanged('id')
      )
      .subscribe(this.onSelectedMachineChanged);
    this.currentSubscription = this.authentication.getCurrentSubscription();
  }

  public getIconNameForMachineType(): string {
    return MachineType.getIconNameForMachineType(this.machine.equipmentKind);
  }

  public closeMachineDetail(): void {
    this.planningContext.selectItem(null);
  }

  public isMachineOfTypeCarpetLoom(): boolean {
    return [MachineType.CARPET_LOOM, MachineType.CARPET_LOOM_NO_JQ, MachineType.VELVET, MachineType.VELVET_NO_JQ].includes(this.machine.equipmentKind);
  }

  public isMachineOfTypeWeaving(): boolean {
    return this.machine.equipmentKind === MachineType.WEAVING_MACHINE;
  }

  public isMachineOfTypeTufting(): boolean {
    return this.machine.equipmentKind === MachineType.TUFT;
  }

  public isMachineOfTypePlastic(): boolean {
    return this.machine.equipmentKind === MachineType.PLASTIC_MACHINE;
  }

  public canShowFooter(): boolean {
    let permission: Permission;
    if (this.isMachineOfTypeCarpetLoom()) {
      return true;
    } else if (this.isMachineOfTypeWeaving()) {
      permission = Permission.TEXFAB_WEAVE_QUEUE;
    } else if (this.isMachineOfTypeTufting()) {
      permission = Permission.TEXFAB_TUFT_QUEUE;
    } else if (this.isMachineOfTypePlastic()) {
      permission = Permission.TEXFAB_PLASTIC_QUEUE;
    } else {
      return false;
    }

    return this.currentSubscription?.hasPermission(permission);
  }

  public createProductionOrderForMachine(): void {
    let url: string;
    if (this.isMachineOfTypeCarpetLoom()) {
      url = RouteUtils.paths.texFab.productionOrder.addProductionOrderForMachine.absolutePath;
    } else if (this.isMachineOfTypeWeaving()) {
      url = RouteUtils.paths.texFab.weaving.addProductionOrderForMachine.absolutePath;
    } else if (this.isMachineOfTypeTufting()) {
      url = RouteUtils.paths.texFab.tufting.addProductionOrderForMachine.absolutePath;
    } else if (this.isMachineOfTypePlastic()) {
      url = RouteUtils.paths.texFab.plastic.addProductionOrder.absolutePath;
    } else {
      return;
    }

    this.navigationHelper.navigateWithRoute({route: url.replace(':id', this.machine.id.toString()), inputData: new IdName(this.machine.id, this.machine.name)});
  }

  private onSelectedMachineChanged = (machine: PlanningEquipment): void => {
    this.machine = machine;
    this.activeItemOnMachine = this.planningContext.planningItemsByMachine[machine.id]?.find((item: PlanningItem) => item.sequenceNumber === 0);
  };
}
